import type { ReactNode } from "react";
import * as React from "react";
import { useAppDispatch } from "utils/hooks";
import { modalAction } from "ui-modules/modals/modalSlice";
import styles from "./Modal.styles";
import { AnimatePresence, motion } from "framer-motion";
import {
  modalMotionVariants,
  backgroundMotionVariants,
} from "utils/animations/animationHelper";
import { MotionVariantType } from "types/enums";
import { MdClose } from "react-icons/md";

type DefaultHeaderProps = {
  buttons?: React.ReactNode;
  title?: string;
  modalName: string;
  callback?: any;
};

type DefaultFooterProps = {
  modalName: string;
};

type ModalProps = {
  title?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  showCommandButtons?: boolean;
  clickOverlayToClose?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  body?: ReactNode;
  modalSize?: string;
  modalName: string;
  disableContentScrolling?: boolean;
  closeModalCallback?: any;
};

const DefaultHeader: React.FC<DefaultHeaderProps> = (props) => {
  const dispatch = useAppDispatch();
  const setModalClose = () => {
    dispatch(modalAction({ isOpen: false, modalName: props.modalName }));
    if (props.callback) {
      props.callback();
    }
  };

  return (
    <div className={styles.headerRow}>
      <div className={styles.titleRow}>
        <span className={styles.span}>{props.title}</span>
      </div>
      <div className={styles.buttonContainer}>
        <button onClick={setModalClose} className="pr-4 pt-3">
          <MdClose className="font-bold poppins h-6 w-6 dark:text-gray-50" />
        </button>
      </div>
    </div>
  );
};

const DefaultFooter: React.FC<DefaultFooterProps> = (props) => {
  const dispatch = useAppDispatch();
  const setModalClose = () =>
    dispatch(modalAction({ isOpen: false, modalName: props.modalName }));

  return (
    <div className={styles.footerContainer}>
      <button onClick={setModalClose} className={styles.closeButton}>
        Close
      </button>
    </div>
  );
};

const Modal: React.FC<ModalProps> = ({
  hideHeader = false,
  hideFooter = false,
  //showCommandButtons = true,
  //clickOverlayToClose = true,
  title = "Modal",
  modalSize = "",
  header,
  footer,
  body,
  modalName,
  disableContentScrolling = false,
  closeModalCallback,
}) => {
  const nodeRef = React.useRef(null);

  const innerModal = (
    <div className={`${styles.innerModalContainer}`} data-testid="innerModal">
      <header
        ref={nodeRef}
        className={hideHeader ? "hidden" : styles.showInnerModalHeader}
        id="modalHeader"
      >
        {header ? (
          header
        ) : (
          <DefaultHeader
            title={title}
            modalName={modalName}
            callback={closeModalCallback}
          />
        )}
      </header>
      <main
        data-testid="modalBody"
        className={disableContentScrolling ? "" : styles.showInnerModalBody}
      >
        {body}
      </main>
      <footer
        className={hideFooter ? "hidden" : styles.showInnerModalFooter}
        data-testid="modalFooter"
      >
        {footer ? footer : <DefaultFooter modalName={modalName} />}
      </footer>
    </div>
  );

  const useOnClickOutside = (event: React.SyntheticEvent) => {
    const modal = document.getElementById("modalBackground");
    if (event.target === modal) {
      // TODO: Implement click outside behavior if needed
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className={styles.modalBackground}
        variants={backgroundMotionVariants}
        initial={MotionVariantType.HIDDEN}
        animate={MotionVariantType.VISIBLE}
        exit={{ opacity: 0, pointerEvents: "none" }}
        onClick={useOnClickOutside}
        onKeyDown={(e) => e.key === "Escape" && useOnClickOutside(e)}
        role="presentation"
        id="modalBackground"
      >
        <motion.div
          className={`${styles.modalContent} ${
            modalSize
              ? modalSize.toString()
              : styles.modalContentDefaultSize
          }`}
          variants={modalMotionVariants}
          initial={MotionVariantType.HIDDEN}
          animate={MotionVariantType.VISIBLE}
          id="modalContent"
        >
          {innerModal}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
export default Modal;
